import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../../utils/classNames";
import IconButton from "../../../atoms/IconButton";
import { MenuIcon, CloseIcon } from "../../../atoms/Iconography/NavigationControls.jsx";

import "./MobileNavigation.scss";

export function MobileHeader({ isOpen, onToggle, slotRef, activeLabel }) {
    const { t } = useTranslation();
    const headerRef = useRef(null);
    const slotElRef = useRef(null);
    // When no page tabs are portaled into the slot, show the centered brand.
    const [slotEmpty, setSlotEmpty] = useState(true);

    // Publish the live header height so scrolling pages can offset their
    // content beneath the fixed translucent header (and content scrolls under
    // it). The height is dynamic — it grows when a page's section tabs are
    // portaled into the slot.
    useEffect(() => {
        const el = headerRef.current;
        if (!el || typeof window === "undefined") {
            return undefined;
        }
        const root = document.documentElement;
        const applyHeight = () => {
            root.style.setProperty("--mobile-header-height", `${el.offsetHeight}px`);
        };
        applyHeight();
        let observer;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(applyHeight);
            observer.observe(el);
        }
        return () => {
            if (observer) {
                observer.disconnect();
            }
            root.style.removeProperty("--mobile-header-height");
        };
    }, []);

    // Track whether the slot has portaled content (section tabs).
    const setSlotEl = useCallback((node) => {
        slotElRef.current = node;
        if (typeof slotRef === "function") {
            slotRef(node);
        } else if (slotRef) {
            slotRef.current = node;
        }
    }, [slotRef]);

    useEffect(() => {
        const slot = slotElRef.current;
        if (!slot || typeof MutationObserver === "undefined") {
            return undefined;
        }
        const update = () => setSlotEmpty(slot.childElementCount === 0);
        update();
        const observer = new MutationObserver(update);
        observer.observe(slot, { childList: true });
        return () => observer.disconnect();
    }, []);

    return (
        <header
            ref={headerRef}
            className={classNames("mobile-header", { "mobile-header--open": isOpen })}
        >
            <IconButton
                icon={isOpen ? <CloseIcon /> : <MenuIcon />}
                label={isOpen ? t("navigation.close") : t("navigation.open")}
                onClick={onToggle}
                variant="ghost"
                size={44}
                className="mobile-header__menu"
            />
            {/* Centered page title when the page has no tabs to portal into the slot. */}
            {slotEmpty && activeLabel && (
                <div className="mobile-header__title" aria-live="polite">
                    {activeLabel}
                </div>
            )}
            {/* Slot the active page's SectionTabbar (tabs + actions) portals into. */}
            <div className="mobile-header__slot" ref={setSlotEl} />
        </header>
    );
}

MobileHeader.propTypes = {
    isOpen: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    slotRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    activeLabel: PropTypes.string,
};

MobileHeader.defaultProps = {
    isOpen: false,
    slotRef: undefined,
    activeLabel: undefined,
};
